import React, { useState, useEffect } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Tabs from '@cloudscape-design/components/tabs';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Badge from '@cloudscape-design/components/badge';
import TextFilter from '@cloudscape-design/components/text-filter';
import Popover from '@cloudscape-design/components/popover';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Select from '@cloudscape-design/components/select';
import Modal from '@cloudscape-design/components/modal';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Textarea from '@cloudscape-design/components/textarea';
import Alert from '@cloudscape-design/components/alert';
import DatePicker from '@cloudscape-design/components/date-picker';
import { MetricsResponse, MissingMetric, CustomMetric, RecommendationResponse, RecommendationDisposition, getApprover1, recordUsageEvent } from '../apis/metricsApi';
import { isApprover } from '../apis/midwayAuth';
import { CENTRAL_TEAM } from '../constants';

interface Props {
  data: MetricsResponse;
  onFeedback: (metricName: string, helpful: string, reason?: string) => void;
  customMetrics?: CustomMetric[];
  currentAlias?: string;
  onSubmitCustomMetric?: (entry: { metricName: string; metricLink: string; justification: string; approver2: string }) => Promise<void>;
  onReviewCustomMetric?: (id: string, status: 'approved' | 'rejected') => Promise<void>;
  onEditCustomMetric?: (id: string, entry: { metricName: string; metricLink: string; justification: string }) => Promise<void>;
  onDeleteCustomMetric?: (id: string) => Promise<void>;
  recResponses?: RecommendationResponse[];
  onRespondToRecommendation?: (entry: { metricName: string; disposition: RecommendationDisposition; eta?: string; note?: string; reason?: string }) => Promise<void>;
}

// --- Helpers ---
// Accept the new label plus the legacy 'AUTO-ROLLBACK-EXISTING' for data uploaded before the rename.
const isExistingOnly = (m: any) => m.priority === 'METRICS-EXIST' || m.priority === 'AUTO-ROLLBACK-EXISTING';
const isAutoRollbackSection = (m: any) => m.section === 'auto-rollback';
const isRecommended = (m: any) => (m.priority === 'recommended' || m.priority === 'P0' || m.priority === 'P1') && !isExistingOnly(m) && !isAutoRollbackSection(m);
const isNiceToHave = (m: any) => (m.priority === 'nice-to-have' || m.priority === 'P2') && !isAutoRollbackSection(m) && m.tag !== 'AUTO-ROLLBACK' && !isExistingOnly(m);
const isAutoRollback = (m: any) => isAutoRollbackSection(m) || m.tag === 'AUTO-ROLLBACK' || isExistingOnly(m);

const getAgentSpacesUrl = (metric: MissingMetric, pipeline: string): string => {
  const prompt = [
    `Implement the following business metric for the ${pipeline} service.`,
    ``,
    `Metric name: ${metric.name}`,
    `What it tracks: ${metric.what || ''}`,
    ``,
    `Code location: ${metric.where || ''}`,
    ``,
    `Implementation guidance:`,
    `${metric.implement || ''}`,
    ``,
    `Instructions:`,
    `1. Pull the ${pipeline} package into your workspace`,
    `2. Read the file and line mentioned above to understand the current code`,
    `3. Look at how other custom metrics are emitted in this service (search for addCount or MetricsContext)`,
    `4. Add the new metric following the same pattern`,
    `5. Build and verify with brazil-build release`,
    `6. Create a CR with the changes`,
  ].join('\n');

  const params = new URLSearchParams({
    mode: 'CODE',
    blueprint: 'AgentSpacesEmptyBlueprintProd',
    spaceName: `Add metric: ${metric.name}`,
    requesterId: 'business-metrics-recommender',
    trackingId: `bmr-${pipeline}-${metric.name}`,
    initialPrompt: prompt,
  });

  return `https://agentspaces.amazon.dev/app/?${params.toString()}`;
};

const getBulkAgentSpacesUrl = (metrics: MissingMetric[], pipeline: string): string => {
  const metricsList = metrics.map((m, i) => [
    `--- Metric ${i + 1}: ${m.name} ---`,
    `What: ${m.what || ''}`,
    `Where: ${m.where || ''}`,
    `Implement: ${m.implement || ''}`,
    '',
  ].join('\n')).join('\n');

  const prompt = [
    `Implement ${metrics.length} business metrics for the ${pipeline} service.`,
    ``,
    `Instructions:`,
    `1. Pull the ${pipeline} package into your workspace`,
    `2. Look at how existing custom metrics are emitted (search for addCount or MetricsContext)`,
    `3. For EACH metric below, add the instrumentation at the specified code location`,
    `4. Follow the same pattern as existing metrics in the service`,
    `5. Build and verify with brazil-build release`,
    `6. Create a single CR with all metric additions`,
    ``,
    `=== METRICS TO IMPLEMENT ===`,
    ``,
    metricsList,
  ].join('\n');

  // Truncate if too long for URL (max ~8000 chars for URL)
  const truncatedPrompt = prompt.length > 6000 ? prompt.substring(0, 6000) + '\n\n... (truncated — see remaining metrics in the BMR tool)' : prompt;

  const params = new URLSearchParams({
    mode: 'CODE',
    blueprint: 'AgentSpacesEmptyBlueprintProd',
    spaceName: `Implement ${metrics.length} metrics: ${pipeline}`,
    requesterId: 'business-metrics-recommender',
    trackingId: `bmr-bulk-${pipeline}-${metrics.length}`,
    initialPrompt: truncatedPrompt,
  });

  return `https://agentspaces.amazon.dev/app/?${params.toString()}`;
};

const tagLabels: Record<string, string> = {
  'silent-failure': '🚨 Silent Failures',
  'data-quality': '📊 Wrong Decisions',
  'business-visibility': '👁️ Blind Spots',
  'deployment-safety': '🔄 Deploy Safety',
  'compliance-risk': '🔒 Compliance',
  'capacity-waste': '📦 Capacity Waste',
  'labor-credit-lost': '👷 Labor Lost',
  'missed-optimization': '⚡ Optimization',
  'SAFETY': '🚨 Silent Failures',
  'AVAILABILITY': '📊 Wrong Decisions',
  'BUSINESS': '👁️ Blind Spots',
  'AUTO-ROLLBACK': '🔄 Deploy Safety',
};

const tagColor = (tag: string): 'red' | 'blue' | 'grey' | 'green' => {
  if (tag === 'silent-failure' || tag === 'SAFETY') return 'red';
  if (tag === 'data-quality' || tag === 'AVAILABILITY') return 'blue';
  if (tag === 'deployment-safety' || tag === 'AUTO-ROLLBACK') return 'blue';
  return 'grey';
};

// --- Main Component ---
export const MetricsDashboard: React.FC<Props> = ({ data, onFeedback, customMetrics = [], currentAlias = '', onSubmitCustomMetric, onReviewCustomMetric, onEditCustomMetric, onDeleteCustomMetric, recResponses = [], onRespondToRecommendation }) => {
  const [activeTab, setActiveTab] = useState('recommended');
  const [highlightedMetric, setHighlightedMetric] = useState<string | null>(null);
  const [cameFromRollback, setCameFromRollback] = useState(false);
  const [autoOpenAdd, setAutoOpenAdd] = useState(false); // jump to Auto-Rollback and open the add-metric dialog

  // Navigate to a recommended metric from auto-rollback tab
  const handleNavigateToMetric = (metricName: string) => {
    setHighlightedMetric(metricName);
    setCameFromRollback(true);
    setActiveTab('recommended');
    // Scroll to metric after tab switch
    setTimeout(() => {
      const el = document.getElementById(`metric-${metricName}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Go back to auto-rollback tab
  const handleBackToRollback = () => {
    setActiveTab('auto-rollback');
    setHighlightedMetric(null);
    setCameFromRollback(false);
  };

  // Compute metric groups
  const recommendedMetrics = data.metrics.filter(isRecommended);
  const niceToHaveMetrics = data.metrics.filter(isNiceToHave);
  const autoRollbackMetrics = data.metrics.filter(isAutoRollback);

  // Summary stats
  const serviceCount = recommendedMetrics.filter(m => m.section === 'service').length;
  const depCount = recommendedMetrics.filter(m => m.section === 'dependency').length;
  const silentCount = recommendedMetrics.filter(m => m.tag === 'silent-failure' || m.tag === 'SAFETY').length;
  const blindCount = recommendedMetrics.filter(m => m.tag === 'business-visibility' || m.tag === 'BUSINESS').length;
  const approvedCustomCount = customMetrics.filter(c => c.status === 'approved').length;

  return (
    <SpaceBetween size="l">
      {/* Summary Section */}
      <SummarySection
        recommended={recommendedMetrics.length}
        niceToHave={niceToHaveMetrics.length}
        autoRollback={autoRollbackMetrics.length}
        serviceCount={serviceCount}
        depCount={depCount}
        silentCount={silentCount}
        blindCount={blindCount}
        totalMonitors={data.autoRollbackAnalysis?.existingMonitors?.totalConfigured || 0}
        hasBusinessMetrics={(data.autoRollbackAnalysis?.businessMetricsAlreadyInRollback?.found || false) || approvedCustomCount > 0}
        onReviewEdit={onSubmitCustomMetric ? () => { setActiveTab('auto-rollback'); setAutoOpenAdd(true); } : undefined}
      />

      {/* Main Tabs */}
      <Container header={
        <Header variant="h2" info={
          <Popover
            header="Understanding the categories"
            content={
              <SpaceBetween size="s">
                <Box><Box fontWeight="bold">Recommended</Box>Business metrics that should exist but don't. Without them, you cannot answer: "Is my service producing correct business outcomes right now?" These detect issues BEFORE customers notice.</Box>
                <Box><Box fontWeight="bold">Nice-to-have</Box>Business insights that improve understanding and planning — helps answer "How is my service performing over time?" but not critical for detecting active issues.</Box>
                <Box><Box fontWeight="bold">Auto-Rollback</Box>Business metrics that should trigger automatic deployment rollback. Your current rollback monitors only detect "service is DOWN" — these detect "service is UP but producing WRONG business results."</Box>
                <Box variant="h4" padding={{ top: 's' }}>Within Recommended:</Box>
                <Box><Box fontWeight="bold">🚨 Silent Failures</Box>Service completes the operation successfully (no errors, no exceptions) but the business outcome is WRONG — items in wrong state, data lost, decisions skipped.</Box>
                <Box><Box fontWeight="bold">📊 Wrong Decisions</Box>A dependency returns bad/stale data on a SUCCESSFUL call. The service trusts this data and makes a wrong business decision.</Box>
                <Box><Box fontWeight="bold">👁️ Blind Spots</Box>No failure is happening, but the team cannot answer basic business questions about state distribution or expected outcomes.</Box>
              </SpaceBetween>
            }
          >
            <Link variant="info">Info</Link>
          </Popover>
        }>Metrics Analysis</Header>
      }>
      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => { setActiveTab(detail.activeTabId); setHighlightedMetric(null); setCameFromRollback(false); }}
        tabs={[
          {
            id: 'recommended',
            label: `Recommended (${recommendedMetrics.length})`,
            content: <MetricsTabContent metrics={recommendedMetrics} onFeedback={onFeedback} feedbackMap={data.feedback} feedbackReasonsMap={data.feedbackReasons} showTagFilter showSectionFilter highlightedMetric={highlightedMetric} cameFromRollback={cameFromRollback} onBackToRollback={handleBackToRollback} pipeline={data.pipeline} />,
          },
          {
            id: 'nice-to-have',
            label: `Nice-to-have (${niceToHaveMetrics.length})`,
            content: <MetricsTabContent metrics={niceToHaveMetrics} onFeedback={onFeedback} feedbackMap={data.feedback} feedbackReasonsMap={data.feedbackReasons} showSectionFilter pipeline={data.pipeline} />,
          },
          {
            id: 'auto-rollback',
            label: `🔄 Auto-Rollback (${autoRollbackMetrics.length})`,
            content: <AutoRollbackTab metrics={autoRollbackMetrics} analysis={data.autoRollbackAnalysis} onFeedback={onFeedback} feedbackMap={data.feedback} allMetrics={data.metrics} onNavigateToMetric={handleNavigateToMetric} pipeline={data.pipeline} customMetrics={customMetrics} currentAlias={currentAlias} onSubmitCustomMetric={onSubmitCustomMetric} onReviewCustomMetric={onReviewCustomMetric} onEditCustomMetric={onEditCustomMetric} onDeleteCustomMetric={onDeleteCustomMetric} recResponses={recResponses} onRespondToRecommendation={onRespondToRecommendation} autoOpenAdd={autoOpenAdd} onAutoOpenHandled={() => setAutoOpenAdd(false)} />,
          },
        ]}
      />
      </Container>
    </SpaceBetween>
  );
};

// --- Summary Section ---
const SummarySection: React.FC<{
  recommended: number; niceToHave: number; autoRollback: number;
  serviceCount: number; depCount: number; silentCount: number; blindCount: number;
  totalMonitors: number; hasBusinessMetrics: boolean; onReviewEdit?: () => void;
}> = ({ recommended, niceToHave, autoRollback, serviceCount, depCount, silentCount, blindCount, totalMonitors, hasBusinessMetrics, onReviewEdit }) => (
  <Container>
    <ColumnLayout columns={4} variant="text-grid">
      <div>
        <Box variant="awsui-key-label">Recommended</Box>
        <Box fontSize="heading-xl" fontWeight="bold" color="text-status-warning">{recommended}</Box>
        <Box fontSize="body-s" color="text-body-secondary">
          {serviceCount} service · {depCount} dependency
        </Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Nice-to-have</Box>
        <Box fontSize="heading-xl" fontWeight="bold" color="text-status-info">{niceToHave}</Box>
        <Box fontSize="body-s" color="text-body-secondary">
          Improve understanding &amp; planning
        </Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Auto-Rollback Metrics</Box>
        <Box fontSize="heading-xl" fontWeight="bold" color="text-status-error">{autoRollback}</Box>
        <Box fontSize="body-s" color="text-body-secondary">
          Recommended to guard deployments
        </Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Existing Auto-Rollback Metrics</Box>
        <Box fontSize="heading-xl" fontWeight="bold">{totalMonitors}</Box>
        <Box fontSize="body-s" color="text-body-secondary">
          Currently configured on the pipeline
        </Box>
        {hasBusinessMetrics ? (
          <Box fontSize="body-s" color="text-status-success">API, Infra, Business</Box>
        ) : (
          <Box fontSize="body-s" color="text-body-secondary">Infra, API</Box>
        )}
        {onReviewEdit && (
          <Box padding={{ top: 'xxs' }}>
            <Link onFollow={(e) => { e.preventDefault(); onReviewEdit(); }}>Review / edit →</Link>
          </Box>
        )}
      </div>
    </ColumnLayout>
  </Container>
);

// --- Metrics Tab Content (shared between Recommended & Nice-to-have) ---
const MetricsTabContent: React.FC<{
  metrics: MissingMetric[];
  onFeedback: (name: string, val: string, reason?: string) => void;
  feedbackMap?: Record<string, string>;
  feedbackReasonsMap?: Record<string, string>;
  showTagFilter?: boolean;
  showSectionFilter?: boolean;
  highlightedMetric?: string | null;
  cameFromRollback?: boolean;
  onBackToRollback?: () => void;
  pipeline: string;
}> = ({ metrics, onFeedback, feedbackMap, feedbackReasonsMap, showTagFilter, showSectionFilter, highlightedMetric, cameFromRollback, onBackToRollback, pipeline }) => {
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(new Set());

  const toggleMetric = (name: string) => {
    setSelectedMetrics(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const selectAll = () => {
    const allVisibleSelected = filtered.length > 0 && filtered.every(m => selectedMetrics.has(m.name));
    if (allVisibleSelected) {
      setSelectedMetrics(new Set());
    } else {
      setSelectedMetrics(new Set(filtered.map(m => m.name)));
    }
  };

  // Filter metrics
  const filtered = metrics.filter(m => {
    if (search && !JSON.stringify(m).toLowerCase().includes(search.toLowerCase())) return false;
    if (sectionFilter === 'service' && m.section !== 'service') return false;
    if (sectionFilter === 'dependency' && m.section !== 'dependency') return false;
    if (tagFilter !== 'all' && m.tag !== tagFilter) return false;
    return true;
  });

  // Compute tag counts scoped to active section filter
  const sectionScopedMetrics = sectionFilter === 'all' ? metrics : metrics.filter(m => m.section === sectionFilter);
  const tagMap: Record<string, number> = {};
  sectionScopedMetrics.forEach(m => {
    const tag = m.tag || 'other';
    tagMap[tag] = (tagMap[tag] || 0) + 1;
  });

  // Compute section counts scoped to active tag filter
  const tagScopedMetrics = tagFilter === 'all' ? metrics : metrics.filter(m => m.tag === tagFilter);

  return (
    <SpaceBetween size="m">
      {/* Filter Bar */}
      <SpaceBetween size="s">
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <TextFilter
            filteringText={search}
            onChange={({ detail }) => setSearch(detail.filteringText)}
            filteringPlaceholder="Search metrics..."
            countText={`${filtered.length} of ${metrics.length}`}
          />
          <div />
        </Grid>
        <SpaceBetween size="xs" direction="horizontal">
          {showSectionFilter && (
            <SegmentedControl
              selectedId={sectionFilter}
              onChange={({ detail }) => { setSectionFilter(detail.selectedId); setTagFilter('all'); }}
              options={[
                { id: 'all', text: `All (${tagScopedMetrics.length})` },
                { id: 'service', text: `⚙️ Service (${tagScopedMetrics.filter(m => m.section === 'service').length})` },
                { id: 'dependency', text: `🔗 Dependency (${tagScopedMetrics.filter(m => m.section === 'dependency').length})` },
              ]}
            />
          )}
          {showTagFilter && Object.keys(tagMap).length > 1 && (
            <SegmentedControl
              selectedId={tagFilter}
              onChange={({ detail }) => setTagFilter(detail.selectedId)}
              options={[
                { id: 'all', text: `All types (${sectionScopedMetrics.length})` },
                ...Object.entries(tagMap).map(([tag, count]) => ({
                  id: tag,
                  text: `${tagLabels[tag] || tag} (${count})`
                }))
              ]}
            />
          )}
        </SpaceBetween>
      </SpaceBetween>

      {/* Bulk action: Implement selected metrics */}
      {(() => {
        const effectiveSelection = filtered.filter(m => selectedMetrics.has(m.name));
        const allVisibleSelected = filtered.length > 0 && filtered.every(m => selectedMetrics.has(m.name));
        return (
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Button variant="link" onClick={selectAll}>
              {allVisibleSelected ? 'Deselect all' : 'Select all'}
            </Button>
            {effectiveSelection.length > 0 ? (
              <Link
                href={getBulkAgentSpacesUrl(effectiveSelection, pipeline)}
                external
                variant="primary"
                onFollow={() => recordUsageEvent('CreateCRForSelected', { pipeline, count: effectiveSelection.length })}
              >
                🤖 Create CR for {effectiveSelection.length} selected
              </Link>
            ) : (
              <Badge color="blue">🤖 Use checkboxes below to select metrics, to create a CR</Badge>
            )}
            {effectiveSelection.length > 0 && (
              <Box fontSize="body-s" color="text-body-secondary">{effectiveSelection.length} of {filtered.length} selected</Box>
            )}
          </SpaceBetween>
        );
      })()}

      {/* Metric Cards */}
      <SpaceBetween size="s">
        {filtered.map((m, i) => (
          <div key={m.name} id={`metric-${m.name}`}>
            <CompactMetricCard
              index={i + 1}
              metric={m}
              feedback={feedbackMap?.[m.name]}
              feedbackReason={feedbackReasonsMap?.[m.name]}
              onFeedback={onFeedback}
              autoExpand={highlightedMetric === m.name}
              onBackToRollback={highlightedMetric === m.name && cameFromRollback ? onBackToRollback : undefined}
              pipeline={pipeline}
              selected={selectedMetrics.has(m.name)}
              onToggleSelect={() => toggleMetric(m.name)}
            />
          </div>
        ))}
      </SpaceBetween>

      {filtered.length === 0 && (
        <Box textAlign="center" padding="l" color="text-body-secondary">
          No metrics match your filters.
        </Box>
      )}
    </SpaceBetween>
  );
};

// --- Compact Metric Card (collapsed by default) ---
const CompactMetricCard: React.FC<{
  index: number;
  metric: MissingMetric;
  feedback?: string;
  feedbackReason?: string;
  onFeedback: (name: string, val: string, reason?: string) => void;
  autoExpand?: boolean;
  onBackToRollback?: () => void;
  pipeline: string;
  selected?: boolean;
  onToggleSelect?: () => void;
}> = ({ index, metric: m, feedback, feedbackReason, onFeedback, autoExpand, onBackToRollback, pipeline, selected, onToggleSelect }) => {
  const [expanded, setExpanded] = useState(autoExpand || false);
  const [dislikeOpen, setDislikeOpen] = useState(false);
  const [dislikeReason, setDislikeReason] = useState('');
  const [dislikeError, setDislikeError] = useState('');
  const MIN_REASON = 20;

  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              <Badge color={tagColor(m.tag || '')}>{tagLabels[m.tag || ''] || m.tag || 'other'}</Badge>
              <Badge color="grey">{m.section === 'service' ? '⚙️ Service' : '🔗 Dependency'}</Badge>
              <Button variant="icon" iconName={expanded ? 'angle-up' : 'angle-down'} onClick={() => setExpanded(!expanded)} ariaLabel={expanded ? 'Collapse' : 'Expand'} />
            </SpaceBetween>
          }
        >
          <span onClick={() => setExpanded(!expanded)} style={{ cursor: 'pointer' }}>
            <SpaceBetween size="xxs" direction="horizontal" alignItems="center">
              {onToggleSelect && (
                <input type="checkbox" checked={selected || false} onChange={onToggleSelect} onClick={(e) => e.stopPropagation()} style={{ width: 16, height: 16, cursor: 'pointer' }} />
              )}
              <Box color="text-body-secondary" fontSize="body-s">#{index}</Box>
              <Box fontWeight="bold"><code>{m.name}</code></Box>
            </SpaceBetween>
          </span>
        </Header>
      }
    >
      {/* Back to Auto-Rollback (inside the highlighted card) */}
      {onBackToRollback && (
        <Box padding={{ bottom: 's' }}>
          <Button variant="link" iconName="arrow-left" onClick={onBackToRollback}>
            Back to Auto-Rollback
          </Button>
        </Box>
      )}

      {/* Always visible: WHAT IS THIS METRIC? */}
      {m.what && (
        <div>
          <Box variant="awsui-key-label" fontSize="body-s">WHAT IS THIS METRIC?</Box>
          <Box>{m.what}</Box>
        </div>
      )}

      {/* Expanded details (why / current / reference code / implement) */}
      {expanded && (
        <SpaceBetween size="m">
          <Box padding={{ top: 'm' }}>
            <SpaceBetween size="m">
              {m.why && (
                <div>
                  <Box variant="awsui-key-label" fontSize="body-s">WHY IT MATTERS</Box>
                  {m.why.includes('• Impact if not fixed:') ? (
                    <>
                      <Box>{m.why.split('• Impact if not fixed:')[0].trim()}</Box>
                      <Box padding={{ top: 'xs' }} color="text-status-error" fontWeight="bold">
                        Impact if not fixed: <Box display="inline" color="text-status-error" fontWeight="normal">{m.why.split('• Impact if not fixed:')[1].trim()}</Box>
                      </Box>
                    </>
                  ) : (
                    <Box>{m.why}</Box>
                  )}
                </div>
              )}
              {m.current && (
                <div>
                  <Box variant="awsui-key-label" fontSize="body-s">CURRENT BEHAVIOR</Box>
                  <Box color="text-status-error">{m.current}</Box>
                </div>
              )}
              {m.where && (
                <div>
                  <Box variant="awsui-key-label" fontSize="body-s">REFERENCE CODE</Box>
                  <Box variant="code" fontSize="body-s">{m.where}</Box>
                </div>
              )}
              {m.implement && (
                <ExpandableSection headerText="🔧 How to implement" variant="footer">
                  <SpaceBetween size="s">
                    <Box fontSize="body-s">{m.implement}</Box>
                    <Link href={getAgentSpacesUrl(m, pipeline)} external variant="primary" onFollow={() => recordUsageEvent('CreateCR', { pipeline })}>
                      🤖 Create CR
                    </Link>
                  </SpaceBetween>
                </ExpandableSection>
              )}
            </SpaceBetween>
          </Box>

          {/* Feedback */}
          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              <Box fontSize="body-s" color="text-body-secondary">Helpful?</Box>
              <Button
                variant={feedback === 'helpful' ? 'primary' : 'normal'}
                iconName="thumbs-up"
                onClick={() => onFeedback(m.name, 'helpful')}
              >Like</Button>
              <Button
                variant={feedback === 'not_helpful' ? 'primary' : 'normal'}
                iconName="thumbs-down"
                onClick={() => { setDislikeReason(feedbackReason || ''); setDislikeError(''); setDislikeOpen(true); }}
              >Dislike</Button>
            </SpaceBetween>
          </Box>
          {feedback === 'not_helpful' && feedbackReason && (
            <Box float="right" fontSize="body-s" color="text-status-warning" padding={{ top: 'xxs' }}>
              Your reason: {feedbackReason}
            </Box>
          )}
        </SpaceBetween>
      )}

      <Modal
        visible={dislikeOpen}
        onDismiss={() => setDislikeOpen(false)}
        header="Why isn’t this a correct recommendation?"
        footer={
          <SpaceBetween size="xs" direction="horizontal">
            <Button variant="link" onClick={() => setDislikeOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => {
                const r = dislikeReason.trim();
                if (r.length < MIN_REASON) {
                  setDislikeError(`Please give a bit more detail — at least ${MIN_REASON} characters (${r.length} so far).`);
                  return;
                }
                onFeedback(m.name, 'not_helpful', r);
                setDislikeOpen(false);
              }}
            >Submit dislike</Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="s">
          <Box fontSize="body-s" color="text-body-secondary">
            Help us improve <code>{m.name}</code> — tell us why this recommendation is wrong or not useful. Minimum {MIN_REASON} characters.
          </Box>
          {dislikeError && <Alert type="error">{dislikeError}</Alert>}
          <FormField label="Reason">
            <Textarea
              value={dislikeReason}
              onChange={({ detail }) => { setDislikeReason(detail.value); setDislikeError(''); }}
              rows={4}
              placeholder="e.g. This path already has a business alarm on OrderSuccessRate, so a separate metric isn’t needed."
            />
          </FormField>
          <Box fontSize="body-s" color={dislikeReason.trim().length < MIN_REASON ? 'text-status-error' : 'text-status-success'}>
            {dislikeReason.trim().length}/{MIN_REASON} characters
          </Box>
        </SpaceBetween>
      </Modal>
    </Container>
  );
};

// --- Auto-Rollback Tab ---
const AutoRollbackTab: React.FC<{
  metrics: MissingMetric[];
  analysis: any;
  onFeedback: (name: string, val: string, reason?: string) => void;
  feedbackMap?: Record<string, string>;
  allMetrics: MissingMetric[];
  onNavigateToMetric: (metricName: string) => void;
  pipeline: string;
  customMetrics?: CustomMetric[];
  currentAlias?: string;
  onSubmitCustomMetric?: (entry: { metricName: string; metricLink: string; justification: string; approver2: string }) => Promise<void>;
  onReviewCustomMetric?: (id: string, status: 'approved' | 'rejected') => Promise<void>;
  onEditCustomMetric?: (id: string, entry: { metricName: string; metricLink: string; justification: string }) => Promise<void>;
  onDeleteCustomMetric?: (id: string) => Promise<void>;
  recResponses?: RecommendationResponse[];
  onRespondToRecommendation?: (entry: { metricName: string; disposition: RecommendationDisposition; eta?: string; note?: string; reason?: string }) => Promise<void>;
  autoOpenAdd?: boolean;
  onAutoOpenHandled?: () => void;
}> = ({ metrics, analysis, onFeedback, feedbackMap, allMetrics, onNavigateToMetric, pipeline, customMetrics = [], currentAlias = '', onSubmitCustomMetric, onReviewCustomMetric, onEditCustomMetric, onDeleteCustomMetric, recResponses = [], onRespondToRecommendation, autoOpenAdd, onAutoOpenHandled }) => {
  const [metricFilter, setMetricFilter] = useState('all');
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null); // null = add mode, id = edit mode
  // Single-metric fields (used only in edit mode)
  const [cmName, setCmName] = useState('');
  const [cmLink, setCmLink] = useState('');
  const [cmJustification, setCmJustification] = useState('');
  const [cmSubmitting, setCmSubmitting] = useState(false);
  const [cmError, setCmError] = useState('');
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Add mode: stage one or more metrics before submitting them all for review
  type NewMetric = { name: string; link: string; justification: string };
  const [newMetrics, setNewMetrics] = useState<NewMetric[]>([{ name: '', link: '', justification: '' }]);
  const [expandedMetricIdx, setExpandedMetricIdx] = useState(0); // which staged metric card is expanded for editing
  const [approver1, setApprover1] = useState(''); // owner-side approver, auto-resolved
  const [approver2, setApprover2] = useState(''); // central-team approver, chosen by submitter
  const [submitSuccess, setSubmitSuccess] = useState<{ count: number; approver1: string; approver2: string } | null>(null);

  // Per-recommendation response drafts (edited inside the Add-business-metric modal)
  // link/justification are used only for the "have my own equivalent metric" case.
  type RespDraft = { disposition: RecommendationDisposition; eta: string; note: string; reason: string; link: string; justification: string };
  const [respDrafts, setRespDrafts] = useState<Record<string, RespDraft>>({});
  const [rowSaving, setRowSaving] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});

  // Auto-open the add-business-metric dialog when asked from the summary tile.
  useEffect(() => {
    if (!autoOpenAdd) return;
    setEditingId(null);
    setCmName(''); setCmLink(''); setCmJustification(''); setCmError('');
    setNewMetrics([{ name: '', link: '', justification: '' }]);
    setExpandedMetricIdx(0);
    setRespDrafts({}); setRowError({});
    setApprover2('');
    getApprover1(pipeline, currentAlias).then(setApprover1);
    setAddModalVisible(true);
    onAutoOpenHandled?.();
  }, [autoOpenAdd]);

  if (!analysis) {
    return <Box textAlign="center" padding="l" color="text-body-secondary">No auto-rollback analysis available.</Box>;
  }

  const canApprove = isApprover(currentAlias);
  const approvedCustom = customMetrics.filter(c => c.status === 'approved');
  const pendingCustom = customMetrics.filter(c => c.status === 'pending');

  // Latest response per recommended metric (for inline display + summary)
  const responseFor = (metricName: string): RecommendationResponse | undefined =>
    recResponses.filter(r => r.metricName === metricName)
      .sort((a, b) => (b.modifiedAt || b.respondedAt || '').localeCompare(a.modifiedAt || a.respondedAt || ''))[0];
  const willImplementResponses = recResponses.filter(r => r.disposition === 'will-implement');
  const notCriticalResponses = recResponses.filter(r => r.disposition === 'not-critical');
  // A user may only submit their own "already have" metric once every recommendation has a response.
  const respondRequired = !!onRespondToRecommendation && metrics.length > 0;
  const unrespondedRecommendations = metrics.filter(m => !responseFor(m.name));
  const allRecommendationsResponded = unrespondedRecommendations.length === 0;

  const resetForm = () => {
    setCmName(''); setCmLink(''); setCmJustification(''); setCmError('');
    setNewMetrics([{ name: '', link: '', justification: '' }]);
    setExpandedMetricIdx(0);
  };

  const openAddModal = () => { setEditingId(null); resetForm(); setRespDrafts({}); setRowError({}); setApprover2(''); getApprover1(pipeline, currentAlias).then(setApprover1); setAddModalVisible(true); };
  const openEditModal = (c: CustomMetric) => {
    setEditingId(c.id);
    setCmName(c.metricName); setCmLink(c.metricLink); setCmJustification(c.justification); setCmError('');
    setAddModalVisible(true);
  };

  const stagedMetricCount = newMetrics.filter(m => m.name.trim() || m.link.trim() || m.justification.trim()).length;
  const updateNewMetric = (i: number, patch: Partial<NewMetric>) => {
    setNewMetrics(prev => prev.map((m, idx) => idx === i ? { ...m, ...patch } : m));
    setCmError('');
  };
  const addNewMetricRow = () => setNewMetrics(prev => { setExpandedMetricIdx(prev.length); return [...prev, { name: '', link: '', justification: '' }]; });
  const removeNewMetricRow = (i: number) => setNewMetrics(prev => {
    if (prev.length <= 1) return prev;
    const next = prev.filter((_, idx) => idx !== i);
    setExpandedMetricIdx(Math.min(i, next.length - 1));
    return next;
  });

  const handleModalSubmit = async () => {
    setCmError('');
    if (!currentAlias) {
      setCmError('Could not determine your alias — try reloading the page.');
      return;
    }
    setCmSubmitting(true);
    try {
      if (editingId) {
        if (!cmName.trim() || !cmLink.trim() || !cmJustification.trim()) {
          setCmError('Metric name, link, and justification are all required.');
          return;
        }
        await onEditCustomMetric?.(editingId, { metricName: cmName.trim(), metricLink: cmLink.trim(), justification: cmJustification.trim() });
      } else {
        // Adding your own metrics is only allowed once every recommendation has a response.
        if (respondRequired && !allRecommendationsResponded) {
          setCmError(`Please respond to all ${metrics.length} recommended auto-rollback metrics below first (${unrespondedRecommendations.length} left).`);
          return;
        }
        const filled = newMetrics.filter(m => m.name.trim() || m.link.trim() || m.justification.trim());
        if (filled.length === 0) {
          setCmError('Add at least one metric (name, link, and justification).');
          return;
        }
        if (filled.some(m => !m.name.trim() || !m.link.trim() || !m.justification.trim())) {
          setCmError('Every staged metric needs a name, link, and justification (or remove the incomplete row).');
          return;
        }
        if (!approver2) {
          setCmError('Select a central-team reviewer (Approver 2) before submitting.');
          return;
        }
        // Submit each staged metric for review.
        for (const m of filled) {
          await onSubmitCustomMetric?.({ metricName: m.name.trim(), metricLink: m.link.trim(), justification: m.justification.trim(), approver2 });
        }
        setSubmitSuccess({ count: filled.length, approver1, approver2 });
      }
      resetForm();
      setEditingId(null);
      setAddModalVisible(false);
    } catch (e: any) {
      setCmError(e?.message || 'Failed to save. Please try again.');
    } finally {
      setCmSubmitting(false);
    }
  };

  const handleReview = async (id: string, status: 'approved' | 'rejected') => {
    setReviewingId(id);
    try {
      await onReviewCustomMetric?.(id, status);
    } finally {
      setReviewingId(null);
    }
  };

  const handleDelete = async (c: CustomMetric) => {
    if (!onDeleteCustomMetric) return;
    if (typeof window !== 'undefined' && !window.confirm(`Delete "${c.metricName}"? This removes it from the list (kept in S3 for audit).`)) return;
    setBusyId(c.id);
    try {
      await onDeleteCustomMetric(c.id);
    } catch (e: any) {
      if (typeof window !== 'undefined') window.alert(e?.message || 'Failed to delete. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  // Current draft for a recommendation row — falls back to any saved response, then defaults.
  const draftFor = (name: string): RespDraft => {
    if (respDrafts[name]) return respDrafts[name];
    const r = responseFor(name);
    return {
      disposition: (r?.disposition as RecommendationDisposition) || 'will-implement',
      eta: r?.eta || '', note: r?.note || '', reason: r?.reason || '', link: '', justification: '',
    };
  };
  const setDraft = (name: string, patch: Partial<RespDraft>) => {
    const base = draftFor(name);
    setRespDrafts(prev => ({ ...prev, [name]: { ...base, ...patch } }));
    setRowError(prev => ({ ...prev, [name]: '' }));
  };
  const saveRow = async (name: string) => {
    const d = draftFor(name);
    if (!currentAlias) {
      setRowError(prev => ({ ...prev, [name]: 'Alias unknown — reload the page.' })); return;
    }
    if (d.disposition === 'will-implement' && !d.eta.trim()) {
      setRowError(prev => ({ ...prev, [name]: 'Planned date is required.' })); return;
    }
    if (d.disposition === 'not-critical' && !d.reason.trim()) {
      setRowError(prev => ({ ...prev, [name]: 'A reason is required.' })); return;
    }
    if (d.disposition === 'have-own' && (!d.link.trim() || !d.justification.trim())) {
      setRowError(prev => ({ ...prev, [name]: 'Metric link and justification are required for your equivalent metric.' })); return;
    }
    if (d.disposition === 'have-own' && !approver2) {
      setRowError(prev => ({ ...prev, [name]: 'Select a central-team reviewer (Approver 2) above first.' })); return;
    }
    setRowSaving(name);
    try {
      // For "have my own equivalent", submit that metric for review right here.
      if (d.disposition === 'have-own') {
        await onSubmitCustomMetric?.({ metricName: name, metricLink: d.link.trim(), justification: d.justification.trim(), approver2 });
      }
      await onRespondToRecommendation?.({
        metricName: name,
        disposition: d.disposition,
        eta: d.disposition === 'will-implement' ? d.eta.trim() : undefined,
        note: d.disposition === 'will-implement' ? d.note.trim() : undefined,
        reason: d.disposition === 'not-critical' ? d.reason.trim() : undefined,
      });
      setRowError(prev => ({ ...prev, [name]: '' }));
    } catch (e: any) {
      setRowError(prev => ({ ...prev, [name]: e?.message || 'Failed to save. Please try again.' }));
    } finally {
      setRowSaving(null);
    }
  };

  // Find matching recommended metric for a rollback metric name
  const findRecommendedMatch = (rollbackName: string): string | null => {
    // Direct name match
    const direct = allMetrics.find(m => m.name === rollbackName && m.section !== 'auto-rollback');
    if (direct) return direct.name;
    // Prefix match (require delimiter after prefix)
    const prefix = rollbackName.split('.')[0];
    const match = allMetrics.find(m =>
      (m.name === prefix || m.name.startsWith(prefix + '.') || m.name.startsWith(prefix + '_'))
      && m.section !== 'auto-rollback'
    );
    return match ? match.name : null;
  };

  // --- Derived view data (computed once) ---
  const gaps = [...(analysis.rollbackGaps || [])].sort(
    (a: any, b: any) => (b.severity === 'CRITICAL' ? 1 : 0) - (a.severity === 'CRITICAL' ? 1 : 0)
  );
  const existingCount = metrics.filter(isExistingOnly).length;
  const newCodeCount = metrics.length - existingCount;
  const bizMetrics = analysis.businessMetricsAlreadyInRollback?.metrics || [];
  const bizMetricsCount = bizMetrics.length + approvedCustom.length;
  const totalMonitors = analysis.existingMonitors?.totalConfigured ?? 0;
  const bizInAlarms = (analysis.existingMonitors?.categories?.businessMetrics ?? 0) + approvedCustom.length;
  const catLabels: Record<string, string> = {
    latency: 'Latency', errorRate: 'Errors / faults', hostHealth: 'Host health',
    dependencyHealth: 'Dependency health', sqsHealth: 'Queue health',
    availability: 'Availability', businessMetrics: 'Business outcome',
  };
  const visibleMetrics = metrics.filter(m =>
    metricFilter === 'all' ? true : metricFilter === 'existing' ? isExistingOnly(m) : !isExistingOnly(m)
  );

  return (
    <SpaceBetween size="l">

      {submitSuccess && (
        <Alert type="success" dismissible onDismiss={() => setSubmitSuccess(null)} header={`Submitted ${submitSuccess.count} metric${submitSuccess.count > 1 ? 's' : ''} for review`}>
          Approvers — <b>1 (owner):</b> {submitSuccess.approver1 || 'not required (pipeline not in org table)'} · <b>2 (central):</b> {submitSuccess.approver2}. Both must approve. Track status under “Pending approvals” (top of the page).
        </Alert>
      )}

      {/* 1. RECOMMENDED METRICS TO ADD — first */}
      <Container header={<Header variant="h2" counter={`(${metrics.length})`}>Recommended Metrics to add to auto-rollback</Header>}>
        <SpaceBetween size="s">
          <Box fontSize="body-s" color="text-body-secondary">
            The recommended metrics critical enough to automatically revert a bad deployment. Click a name for full details in the Recommended tab.
            {onRespondToRecommendation && ' To respond to these — commit to implement (with an ETA), say why one isn’t critical, or point us to a metric you already have — use the “Add business metric” button below.'}
          </Box>
          {metrics.length > 0 && (
            <SegmentedControl
              selectedId={metricFilter}
              onChange={({ detail }) => setMetricFilter(detail.selectedId)}
              options={[
                { id: 'all', text: `All (${metrics.length})` },
                { id: 'new', text: `⚠️ Needs new code (${newCodeCount})` },
                { id: 'existing', text: `✅ Add alarm only (${existingCount})` },
              ]}
            />
          )}
          {visibleMetrics.length === 0 ? (
            <Box color="text-body-secondary" padding="s">No metrics in this group.</Box>
          ) : (
            <SpaceBetween size="xs">
              {visibleMetrics.map((m, i) => {
                const matchName = findRecommendedMatch(m.name);
                const resp = responseFor(m.name);
                return (
                  <div key={m.name} style={{ borderBottom: i < visibleMetrics.length - 1 ? '1px solid var(--color-border-divider-default)' : 'none', paddingBottom: 8 }}>
                    <SpaceBetween size="xxs">
                      <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                        {isExistingOnly(m)
                          ? <Badge color="green">✅ Add alarm</Badge>
                          : <Badge color="grey">⚠️ New code</Badge>}
                        <Box fontWeight="bold" fontSize="body-s">
                          {matchName
                            ? <Link onFollow={(e) => { e.preventDefault(); onNavigateToMetric(matchName); }}><code>{m.name}</code></Link>
                            : <code>{m.name}</code>}
                        </Box>
                        {resp?.disposition === 'will-implement' && <Badge color="blue">👍 Will implement{resp.eta ? ` · ETA ${resp.eta}` : ''}</Badge>}
                        {resp?.disposition === 'not-critical' && <Badge color="red">🚫 Not critical</Badge>}
                        {resp?.disposition === 'have-own' && <Badge color="green">✔️ Have equivalent metric</Badge>}
                      </SpaceBetween>
                      <Box fontSize="body-s" color="text-body-secondary">{m.what ? m.what.split('.')[0] + '.' : ''}</Box>
                      {resp?.disposition === 'not-critical' && resp.reason && <Box fontSize="body-s" color="text-status-warning">Reason: {resp.reason}</Box>}
                    </SpaceBetween>
                  </div>
                );
              })}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>

      {/* 2. DEPLOYMENT RISKS — worst first, first one open */}
      {gaps.length > 0 && (
        <Container header={<Header variant="h2" counter={`(${gaps.length})`}>🔴 Deployment risks your rollback can&apos;t catch</Header>}>
          <SpaceBetween size="s">
            {gaps.map((gap: any, i: number) => (
              <ExpandableSection
                key={i}
                defaultExpanded={i === 0}
                variant="container"
                headerText={`${gap.severity === 'CRITICAL' ? '🔴 CRITICAL' : '🟠'} — ${gap.scenario}`}
              >
                <SpaceBetween size="s">
                  <div><Box variant="awsui-key-label">What silently breaks</Box><Box>{gap.whatBreaks}</Box></div>
                  <div><Box variant="awsui-key-label">Why every current alarm stays green</Box><Box color="text-status-error">{gap.whyCurrentAlarmsWontFire}</Box></div>
                  <ColumnLayout columns={2} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Detection time today</Box>
                      <Box fontWeight="bold" color="text-status-error">{gap.howLongUntilDetected || 'Not detected until reported'}</Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Fix — add this metric</Box>
                      <Box fontWeight="bold"><code>{gap.recommendedMetric}</code></Box>
                      <Box padding={{ top: 'xxs' }}>
                        {gap.needsNewCode
                          ? <Badge color="grey">⚠️ Needs new code</Badge>
                          : <Badge color="green">✅ Exists — add alarm</Badge>}
                      </Box>
                    </div>
                  </ColumnLayout>
                </SpaceBetween>
              </ExpandableSection>
            ))}
          </SpaceBetween>
        </Container>
      )}

      {/* 3. BUSINESS METRICS ALREADY IN ROLLBACK — always shown */}
      <Container
        header={
          <Header
            variant="h2"
            counter={`(${bizMetricsCount})`}
            actions={
              onSubmitCustomMetric && (
                <Button iconName="add-plus" onClick={openAddModal}>
                  Add business metric
                </Button>
              )
            }
          >
            Business metrics already in your rollback
          </Header>
        }
      >
        <SpaceBetween size="s">
          <Box fontSize="body-s" color="text-body-secondary">
            Not aligned with our analysis? If you already have the required business metric wired into rollback, use <b>Add business metric</b> — we’ll review it and mark your business-metrics goal complete.
          </Box>

          {/* Detected by analysis */}
          {analysis.businessMetricsAlreadyInRollback?.message && bizMetrics.length > 0 && (
            <Box fontSize="body-s">{analysis.businessMetricsAlreadyInRollback.message}</Box>
          )}
          {bizMetrics.length > 0 && (
            <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 8 }}>
              <SpaceBetween size="s">
                {bizMetrics.map((m: any, i: number) => {
                  const source = m.source || (m.type === 'cumulative_threshold' ? 'redifork_bake' : m.type === 'fuego' ? 'fuego_monitor' : m.inAutoRollbackAlarmTree === false ? 'redifork_bake' : 'carnaval_alarm');
                  const inAlarmTree = m.inAutoRollbackAlarmTree !== false;
                  const sourceLabel = source === 'redifork_bake' ? 'RediFork Bake' : source === 'fuego_monitor' ? 'Fuego Monitor' : source === 'carnaval_alarm' ? 'Carnaval Alarm' : source;
                  return (
                    <div key={i} style={{ borderBottom: '1px solid var(--color-border-divider-default)', paddingBottom: 8 }}>
                      <SpaceBetween size="xxs">
                        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                          <Badge color={inAlarmTree ? 'green' : 'blue'}>{sourceLabel}</Badge>
                          <Box fontWeight="bold" fontSize="body-s">{m.name}</Box>
                        </SpaceBetween>
                        {(m.threshold || m.location) && <Box fontSize="body-s" color="text-body-secondary">{m.threshold || m.location}</Box>}
                        {m.limitation && <Box fontSize="body-s" color="text-status-warning">⚠️ Can&apos;t detect: {m.limitation}</Box>}
                      </SpaceBetween>
                    </div>
                  );
                })}
              </SpaceBetween>
            </div>
          )}

          {bizMetricsCount === 0 && willImplementResponses.length === 0 && notCriticalResponses.length === 0 && pendingCustom.length === 0 && (
            <Box color="text-body-secondary">None yet — your rollback has no business-outcome metrics. The recommendations above are what to add. If you already have one, use “Add business metric”.</Box>
          )}

          {/* USER ADDED — user contributions: added metrics + responses to our recommendations */}
          {(approvedCustom.length > 0 || willImplementResponses.length > 0 || notCriticalResponses.length > 0) && (
            <SpaceBetween size="s">
              <Header variant="h3">User Added</Header>

              {approvedCustom.map((c) => (
                <div key={c.id} style={{ borderBottom: '1px solid var(--color-border-divider-default)', paddingBottom: 8 }}>
                  <SpaceBetween size="xxs">
                    <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                      <Badge color="green">✅ Customer added</Badge>
                      <Box fontWeight="bold" fontSize="body-s">
                        {c.metricLink ? <Link href={c.metricLink} external>{c.metricName}</Link> : c.metricName}
                      </Box>
                    </SpaceBetween>
                    <Box fontSize="body-s" color="text-body-secondary">{c.justification}</Box>
                    <Box fontSize="body-s" color="text-body-secondary">
                      Added by {c.addedBy}{c.reviewedBy ? ` · approved by ${c.reviewedBy}` : ''}{c.modifiedBy ? ` · modified by ${c.modifiedBy}` : ''}
                    </Box>
                    {(onEditCustomMetric || onDeleteCustomMetric) && (
                      <SpaceBetween size="xs" direction="horizontal">
                        {onEditCustomMetric && <Button variant="inline-link" iconName="edit" onClick={() => openEditModal(c)}>Edit</Button>}
                        {onDeleteCustomMetric && <Button variant="inline-link" iconName="remove" loading={busyId === c.id} onClick={() => handleDelete(c)}>Delete</Button>}
                      </SpaceBetween>
                    )}
                  </SpaceBetween>
                </div>
              ))}

              {willImplementResponses.length > 0 && (
                <div>
                  <Box variant="awsui-key-label">👍 Will implement our recommendation ({willImplementResponses.length})</Box>
                  <SpaceBetween size="xxs">
                    {willImplementResponses.map((r, i) => (
                      <Box key={i} fontSize="body-s">
                        <code>{r.metricName}</code>{r.eta ? ` — ETA ${r.eta}` : ''} — by {r.modifiedBy || r.respondedBy}{r.note ? ` · “${r.note}”` : ''}
                      </Box>
                    ))}
                  </SpaceBetween>
                </div>
              )}

              {notCriticalResponses.length > 0 && (
                <div>
                  <Box variant="awsui-key-label">🚫 Marked not critical ({notCriticalResponses.length})</Box>
                  <SpaceBetween size="xxs">
                    {notCriticalResponses.map((r, i) => (
                      <Box key={i} fontSize="body-s" color="text-body-secondary">
                        <code>{r.metricName}</code> — by {r.modifiedBy || r.respondedBy}{r.reason ? `: ${r.reason}` : ''}
                      </Box>
                    ))}
                  </SpaceBetween>
                </div>
              )}
            </SpaceBetween>
          )}

          {/* Pending submissions — visible to everyone, not counted until approved */}
          {pendingCustom.length > 0 && (
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Pending review ({pendingCustom.length})</Box>
              {pendingCustom.map((c) => (
                <div key={c.id} style={{ borderBottom: '1px solid var(--color-border-divider-default)', paddingBottom: 8 }}>
                  <SpaceBetween size="xxs">
                    <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                      <Badge color="grey">🕓 Review pending</Badge>
                      <Box fontWeight="bold" fontSize="body-s">
                        {c.metricLink ? <Link href={c.metricLink} external>{c.metricName}</Link> : c.metricName}
                      </Box>
                    </SpaceBetween>
                    <Box fontSize="body-s" color="text-body-secondary">{c.justification}</Box>
                    <Box fontSize="body-s" color="text-body-secondary">
                      Added by {c.addedBy}{c.modifiedBy ? ` · modified by ${c.modifiedBy}` : ''}
                    </Box>
                    <SpaceBetween size="xs" direction="horizontal">
                      {canApprove && onReviewCustomMetric && (
                        <>
                          <Button variant="primary" loading={reviewingId === c.id} onClick={() => handleReview(c.id, 'approved')}>Approve</Button>
                          <Button loading={reviewingId === c.id} onClick={() => handleReview(c.id, 'rejected')}>Reject</Button>
                        </>
                      )}
                      {onEditCustomMetric && <Button variant="inline-link" iconName="edit" onClick={() => openEditModal(c)}>Edit</Button>}
                      {onDeleteCustomMetric && <Button variant="inline-link" iconName="remove" loading={busyId === c.id} onClick={() => handleDelete(c)}>Delete</Button>}
                    </SpaceBetween>
                  </SpaceBetween>
                </div>
              ))}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>

      {/* 4. HOW CURRENT ROLLBACK WORKS — coverage table + why it isn't enough */}
      <Container header={<Header variant="h2">How your current rollback works — and why it isn&apos;t enough</Header>}>
        <SpaceBetween size="m">
          {analysis.existingMonitors && (
            <SpaceBetween size="s">
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Total monitors</Box>
                  <Box fontSize="heading-l" fontWeight="bold">{totalMonitors}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Business metrics in alarms</Box>
                  <Box fontSize="heading-l" fontWeight="bold" color={bizInAlarms > 0 ? 'text-status-success' : 'text-status-error'}>{bizInAlarms}</Box>
                </div>
              </ColumnLayout>
              {analysis.existingMonitors.categories && (
                <div>
                  <Box variant="awsui-key-label">Monitors by type</Box>
                  <ColumnLayout columns={4} variant="text-grid">
                    {Object.entries(analysis.existingMonitors.categories).map(([cat, count]: [string, any]) => (
                      <div key={cat}>
                        <Box fontSize="body-s" color="text-body-secondary">{catLabels[cat] || cat}</Box>
                        <Box fontWeight="bold">{String(count)}</Box>
                      </div>
                    ))}
                  </ColumnLayout>
                </div>
              )}
            </SpaceBetween>
          )}
          {analysis.currentDetection?.whatCurrentRollbackDetects && (
            <div>
              <Box variant="awsui-key-label">What these monitors DO catch</Box>
              <SpaceBetween size="xs">
                {analysis.currentDetection.whatCurrentRollbackDetects.map((item: any, i: number) => (
                  <Box key={i} fontSize="body-s">✅ <b>{item.category}</b>{item.exampleScenario ? ` — ${item.exampleScenario}` : ''}</Box>
                ))}
              </SpaceBetween>
            </div>
          )}
          {analysis.whyWeRecommend?.whyExistingMonitorsAreNotBusinessMetrics && (
            <div>
              <Box variant="awsui-key-label">Why it isn&apos;t enough</Box>
              <Box fontSize="body-s">{analysis.whyWeRecommend.whyExistingMonitorsAreNotBusinessMetrics}</Box>
            </div>
          )}
        </SpaceBetween>
      </Container>

      {/* Add / edit business-metric modal */}
      <Modal
        visible={addModalVisible}
        onDismiss={() => { setAddModalVisible(false); setEditingId(null); }}
        size="large"
        header={editingId ? 'Edit business metric' : 'Add or respond to business metrics'}
        footer={
          <SpaceBetween size="xs" direction="horizontal">
            <Button variant="link" onClick={() => { setAddModalVisible(false); setEditingId(null); }}>Close</Button>
            <Button variant="primary" loading={cmSubmitting} disabled={!editingId && respondRequired && !allRecommendationsResponded} onClick={handleModalSubmit}>{editingId ? 'Save changes' : (stagedMetricCount > 1 ? `Submit ${stagedMetricCount} metrics for review` : 'Submit metric for review')}</Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          <SpaceBetween size="m">
            <Box fontSize="body-s" color="text-body-secondary">
              {editingId
                ? <>Update this business metric. Your changes are attributed to you (<b>{currentAlias || 'unknown'}</b>).</>
                : <>Add one or more business-outcome metrics you already have wired into {pipeline}&apos;s rollback that this analysis missed. They&apos;ll be marked <b>Review pending</b> until an approver confirms them — then your business-metrics goal is complete.</>}
            </Box>
            {!editingId && respondRequired && !allRecommendationsResponded && (
              <Alert type="info">
                Respond to all {metrics.length} recommended auto-rollback metrics below before submitting your own — {unrespondedRecommendations.length} still need a response.
              </Alert>
            )}
            {cmError && <Alert type="error">{cmError}</Alert>}

            {editingId ? (
              <>
                <FormField label="Metric name">
                  <Input value={cmName} onChange={({ detail }) => { setCmName(detail.value); setCmError(''); }} placeholder="e.g., Decant.ItemsMovedRate" />
                </FormField>
                <FormField label="Metric link" description="Link to the CloudWatch/Carnaval alarm, RediFork bake config, or dashboard.">
                  <Input value={cmLink} onChange={({ detail }) => { setCmLink(detail.value); setCmError(''); }} type="url" placeholder="https://..." />
                </FormField>
                <FormField label="Why is this a business metric?" description="Justify why it measures a business outcome and is wired into rollback.">
                  <Textarea value={cmJustification} onChange={({ detail }) => { setCmJustification(detail.value); setCmError(''); }} rows={4} placeholder="This alarm rolls back the deployment when the decant success rate drops below..." />
                </FormField>
                <FormField label="Modified by">
                  <Input value={currentAlias || '(unknown — reload if blank)'} disabled readOnly />
                </FormField>
              </>
            ) : (
              <>
                {newMetrics.map((nm, i) => {
                  const isOpen = expandedMetricIdx === i;
                  if (!isOpen) {
                    // Collapsed summary — click to edit
                    return (
                      <div key={i} style={{ border: '1px solid var(--color-border-divider-default)', borderRadius: 8, padding: 12 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box fontSize="body-s">
                            <b>Metric {i + 1}:</b> {nm.name ? <code>{nm.name}</code> : <span style={{ color: 'var(--color-text-status-inactive)' }}>(empty)</span>}
                          </Box>
                          <SpaceBetween size="xs" direction="horizontal">
                            <Button variant="inline-link" iconName="edit" onClick={() => setExpandedMetricIdx(i)}>Edit</Button>
                            {newMetrics.length > 1 && <Button variant="inline-link" iconName="remove" onClick={() => removeNewMetricRow(i)}>Remove</Button>}
                          </SpaceBetween>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={i} style={{ border: '1px solid var(--color-border-item-selected, #0972d3)', borderRadius: 8, padding: 12 }}>
                      <SpaceBetween size="xs">
                        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                          <Box fontWeight="bold" fontSize="body-s">Metric {i + 1}</Box>
                          {newMetrics.length > 1 && <Button variant="inline-link" iconName="remove" onClick={() => removeNewMetricRow(i)}>Remove</Button>}
                        </SpaceBetween>
                        <FormField label="Metric name">
                          <Input value={nm.name} onChange={({ detail }) => updateNewMetric(i, { name: detail.value })} placeholder="e.g., Decant.ItemsMovedRate" />
                        </FormField>
                        <FormField label="Metric link" description="Link to the CloudWatch/Carnaval alarm, RediFork bake config, or dashboard.">
                          <Input value={nm.link} onChange={({ detail }) => updateNewMetric(i, { link: detail.value })} type="url" placeholder="https://..." />
                        </FormField>
                        <FormField label="Why is this a business metric?" description="Justify why it measures a business outcome and is wired into rollback.">
                          <Textarea value={nm.justification} onChange={({ detail }) => updateNewMetric(i, { justification: detail.value })} rows={3} placeholder="This alarm rolls back the deployment when the decant success rate drops below..." />
                        </FormField>
                      </SpaceBetween>
                    </div>
                  );
                })}
                <Button iconName="add-plus" onClick={addNewMetricRow}>Add another metric</Button>
                <FormField label="Submitted by">
                  <Input value={currentAlias || '(unknown — reload if blank)'} disabled readOnly />
                </FormField>
                <Box variant="awsui-key-label">Approvals required (both must approve)</Box>
                <FormField label="Approver 1 — pipeline owner" description="Auto-assigned (the pipeline owner, or their manager if you are the owner). Can't be changed.">
                  <Input value={approver1 || '(resolving… / not in org table)'} disabled readOnly />
                </FormField>
                <FormField label="Approver 2 — central team" description="Pick a central-team reviewer.">
                  <Select
                    selectedOption={approver2 ? { label: approver2, value: approver2 } : null}
                    onChange={({ detail }) => { setApprover2(detail.selectedOption.value || ''); setCmError(''); }}
                    options={CENTRAL_TEAM.map(a => ({ label: a, value: a }))}
                    placeholder="Select a reviewer"
                    empty="No reviewers"
                  />
                </FormField>
              </>
            )}
          </SpaceBetween>

          {!editingId && onRespondToRecommendation && metrics.length > 0 && (
            <SpaceBetween size="s">
              <Header variant="h3">Respond to our recommendations ({metrics.length})</Header>
              <Box fontSize="body-s" color="text-body-secondary">
                For each metric we recommend adding to auto-rollback, tell us your plan: commit to implement (with an ETA), say why it isn’t critical, or say you already have an equivalent. Each row saves on its own, recorded against your alias (<b>{currentAlias || 'unknown'}</b>).
              </Box>
              {metrics.map((m) => {
                const d = draftFor(m.name);
                const saved = responseFor(m.name);
                return (
                  <div key={m.name} style={{ border: '1px solid var(--color-border-divider-default)', borderRadius: 8, padding: 14 }}>
                    <SpaceBetween size="s">
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box fontSize="heading-s" fontWeight="bold">
                          <Popover
                            header={m.name}
                            size="large"
                            triggerType="text"
                            dismissButton
                            content={
                              <div style={{ maxHeight: 380, overflowY: 'auto', maxWidth: 520 }}>
                                <SpaceBetween size="xs">
                                  <SpaceBetween size="xs" direction="horizontal">
                                    {m.tag && <Badge color={tagColor(m.tag)}>{tagLabels[m.tag] || m.tag}</Badge>}
                                    {m.priority && <Badge color="grey">{m.priority}</Badge>}
                                    {m.section && <Badge color="blue">{m.section}</Badge>}
                                    {m.type && <Badge color="grey">{m.type}</Badge>}
                                  </SpaceBetween>
                                  {m.what && <div><Box variant="awsui-key-label">What is this metric?</Box><Box fontSize="body-s">{m.what}</Box></div>}
                                  {m.why && <div><Box variant="awsui-key-label">Why it matters</Box><Box fontSize="body-s">{m.why}</Box></div>}
                                  {m.current && <div><Box variant="awsui-key-label">Current behavior</Box><Box fontSize="body-s">{m.current}</Box></div>}
                                  {m.where && <div><Box variant="awsui-key-label">Reference code</Box><Box fontSize="body-s"><code>{m.where}</code></Box></div>}
                                  {m.implement && <div><Box variant="awsui-key-label">How to implement</Box><Box fontSize="body-s">{m.implement}</Box></div>}
                                </SpaceBetween>
                              </div>
                            }
                          >
                            <Link variant="info"><span style={{ fontFamily: 'monospace' }}>{m.name}</span> ⓘ</Link>
                          </Popover>
                        </Box>
                        {saved && (
                          saved.disposition === 'will-implement'
                            ? <Badge color="blue">👍 Will implement{saved.eta ? ` · ETA ${saved.eta}` : ''}</Badge>
                            : saved.disposition === 'not-critical'
                              ? <Badge color="red">🚫 Not critical</Badge>
                              : <Badge color="green">✔️ Have equivalent</Badge>
                        )}
                      </div>
                      {m.what && <Box fontSize="body-s" color="text-body-secondary">{m.what.split('.')[0]}. <Box variant="span" fontSize="body-s" color="text-status-info">(click name for full details)</Box></Box>}
                      <SegmentedControl
                        selectedId={d.disposition}
                        onChange={({ detail }) => setDraft(m.name, { disposition: detail.selectedId as RecommendationDisposition })}
                        options={[
                          { id: 'will-implement', text: '👍 Will implement' },
                          { id: 'not-critical', text: '🚫 Not critical' },
                          { id: 'have-own', text: '✔️ Have my own equivalent metric' },
                        ]}
                      />
                      {d.disposition === 'will-implement' && (
                        <>
                          <FormField label="Planned date">
                            <DatePicker
                              value={d.eta}
                              onChange={({ detail }) => setDraft(m.name, { eta: detail.value })}
                              placeholder="YYYY/MM/DD"
                              isDateEnabled={(date) => date >= new Date(new Date().toDateString())}
                              openCalendarAriaLabel={(sel) => 'Choose planned date' + (sel ? `, selected ${sel}` : '')}
                            />
                          </FormField>
                          <Input value={d.note} onChange={({ detail }) => setDraft(m.name, { note: detail.value })} placeholder="Optional note (e.g. I like this recommendation)" />
                        </>
                      )}
                      {d.disposition === 'not-critical' && (
                        <Textarea value={d.reason} onChange={({ detail }) => setDraft(m.name, { reason: detail.value })} rows={2} placeholder="Why isn’t this a critical business metric to auto-rollback on?" />
                      )}
                      {d.disposition === 'have-own' && (
                        <>
                          <Box fontSize="body-s" color="text-body-secondary">Your equivalent metric for <code>{m.name}</code>. It’s submitted for review under your alias when you save.</Box>
                          <FormField label="Your metric link" description="Link to the CloudWatch/Carnaval alarm, RediFork bake config, or dashboard.">
                            <Input value={d.link} onChange={({ detail }) => setDraft(m.name, { link: detail.value })} type="url" placeholder="https://..." />
                          </FormField>
                          <FormField label="Why is it equivalent / a business metric?">
                            <Textarea value={d.justification} onChange={({ detail }) => setDraft(m.name, { justification: detail.value })} rows={2} placeholder="Already covered by MyService.SuccessRate which auto-rolls-back when..." />
                          </FormField>
                        </>
                      )}
                      {rowError[m.name] && <Box fontSize="body-s" color="text-status-error">{rowError[m.name]}</Box>}
                      <Box>
                        <Button variant="normal" iconName="check" loading={rowSaving === m.name} onClick={() => saveRow(m.name)}>
                          {d.disposition === 'have-own' ? 'Save & submit my equivalent metric' : 'Save response'}
                        </Button>
                      </Box>
                    </SpaceBetween>
                  </div>
                );
              })}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Modal>

    </SpaceBetween>
  );
};
